import { THEME_ID, type ThemeId } from '../../../themes/types';

/**
 * Theme-specific landing "What is …?" copy. Add locales or domains by extending
 * the map or layering locale on top of ThemeId later.
 */
export interface LandingDescriptionCopy {
  eyebrow: string;
  headline: string;
  bodyParagraphs: readonly string[];
  illustrationAlt: string;
}

const KINDLY: LandingDescriptionCopy = {
  eyebrow: 'What is Kindly Human?',
  headline: 'Real people. Real connection.',
  bodyParagraphs: [
    'Life can feel heavy sometimes—but you don’t have to carry it alone.',
    'Kindly Human connects you with real people who understand what it feels like to face life’s challenges. Whether you’re looking for a listening ear, a story that resonates, or perspective to help you move forward, you’ll find support that’s human, personal, and real.',
    'The experiences, tools and resources on Kindly Human are designed to remind you that you’re not alone—and encourage you through the ups and downs of life.',
  ],
  illustrationAlt: 'Kindly Human',
};

const KINDLY_CHRISTIAN: LandingDescriptionCopy = {
  eyebrow: 'What is Kindly Christian?',
  headline: 'Real people. Shared faith. Real connection.',
  bodyParagraphs: [
    'Life can feel isolating at times, but you don’t have to carry it alone.',
    'Kindly Christian connects you with fellow believers who understand what it means to walk through life’s challenges while holding onto faith. Whether you are looking for someone who will listen, a story that strengthens your spirit, or encouragement rooted in shared belief, you will find support that is compassionate, personal, and genuine.',
    'The conversations, stories, and resources within Kindly Christian are here to remind you that you are not alone. Through faith, understanding, and community, you can find encouragement for whatever season of life you’re walking through.',
  ],
  illustrationAlt: 'Kindly Christian',
};

const BY_THEME: Record<ThemeId, LandingDescriptionCopy> = {
  [THEME_ID.KINDLY]: KINDLY,
  [THEME_ID.KINDLY_CHRISTIAN]: KINDLY_CHRISTIAN,
};

export function getLandingDescriptionCopy(themeId: ThemeId): LandingDescriptionCopy {
  return BY_THEME[themeId];
}

/** Benefits block intro ("Meaningful Connections" + two paragraphs with a blank line between). */
export interface BenefitsIntroCopy {
  headline: string;
  bodyParagraphs: readonly [string, string];
}

const BENEFITS_INTRO_KINDLY: BenefitsIntroCopy = {
  headline: 'Meaningful Connections',
  bodyParagraphs: [
    'Sometimes what matters most isn’t what you’re facing, but who is there when you face it. Whether you’re looking for understanding, direction, or simply someone to listen, connection can change the course of a moment.',
    'Kindly Human makes it easy to find the right level of support—right when you need it—through real connection, authentic empathy, and personalized support. Take a moment to explore how these services can help you feel heard, supported, and never alone.',
  ],
};

const BENEFITS_INTRO_KINDLY_CHRISTIAN: BenefitsIntroCopy = {
  headline: 'Meaningful Connections',
  bodyParagraphs: [
    'Sometimes what matters most is not just what you are facing, but who walks beside you through it. Whether you are looking for understanding, faith-centered encouragement, or simply someone who will listen, connection can change the course in a channeling moment.',
    'Kindly Christian makes it easy to find the right level of support right when you need it through shared faith, authentic understanding, and compassionate connection. Take a moment to explore how these conversations, stories, and resources can help you feel encouraged, supported, and never alone.',
  ],
};

const BENEFITS_INTRO_BY_THEME: Record<ThemeId, BenefitsIntroCopy> = {
  [THEME_ID.KINDLY]: BENEFITS_INTRO_KINDLY,
  [THEME_ID.KINDLY_CHRISTIAN]: BENEFITS_INTRO_KINDLY_CHRISTIAN,
};

export function getBenefitsIntroCopy(themeId: ThemeId): BenefitsIntroCopy {
  return BENEFITS_INTRO_BY_THEME[themeId];
}

/** Desktop: full paragraph. Mobile: bold lead + remainder (layout differs between breakpoints). */
export interface ProductWalkthroughExperienceCopy {
  title: string;
  subtitle: string;
  descriptionDesktop: string;
  descriptionMobileBold: string;
  descriptionMobileRest: string;
}

/** Slides with a bold lead line + body (desktop vs mobile body may differ). */
export interface ProductWalkthroughStandardSlideCopy {
  title: string;
  subtitle: string;
  descriptionBold: string;
  descriptionBodyDesktop: string;
  descriptionBodyMobile: string;
}

export interface ProductWalkthroughCopy {
  experience: ProductWalkthroughExperienceCopy;
  peersona: ProductWalkthroughStandardSlideCopy;
  calls: ProductWalkthroughStandardSlideCopy;
  resources: ProductWalkthroughStandardSlideCopy;
}

const PRODUCT_WALKTHROUGH_KINDLY: ProductWalkthroughCopy = {
  experience: {
    title: 'Start Strong',
    subtitle: 'Authentic, relatable perspectives',
    descriptionDesktop:
      "Real voices and real stories remind us we're not alone—each audio and video experience offers understanding, encouragement, and validation to keep moving forward.",
    descriptionMobileBold: "Real voices and real stories remind us we're not alone ",
    descriptionMobileRest:
      '— each audio and video experience offers understanding, encouragement, and validation to keep moving forward.',
  },
  peersona: {
    title: 'By Your Side',
    subtitle: 'Perspective when you need it',
    descriptionBold: 'With human-based Peersonas, connection is immediate',
    descriptionBodyDesktop:
      ' — real AI Chat conversations that meets you anywhere you are, whenever you need to be heard.',
    descriptionBodyMobile:
      '— real AI Chat conversations that meets you anywhere you are, whenever you need to be heard. For the perfect in-the-moment reset.',
  },
  calls: {
    title: 'Authentic Connections',
    subtitle: 'A safe space to talk it out',
    descriptionBold: 'Sometimes the best relief comes from saying things out loud.',
    descriptionBodyDesktop:
      ' Talking with a Kindly Human Peer connects you with someone who listens with empathy and offers perspective.',
    descriptionBodyMobile:
      ' Talking with a Kindly Human Peer connects you with someone who listens with empathy and offers perspective and encouragement when you need it most.',
  },
  resources: {
    title: 'Resources Around You',
    subtitle: 'Unlocking the path forward',
    descriptionBold: "Life doesn't come with a roadmap.",
    descriptionBodyDesktop:
      " The support you need is closer than you think. We connect the dots between Kindly Human and your organization's resources, giving you clear next steps when you need them most.",
    descriptionBodyMobile:
      " The support you need is closer than you think. We connect the dots between Kindly Human and your organization's resources, giving you clear next steps when you need them most.",
  },
};

const PRODUCT_WALKTHROUGH_KINDLY_CHRISTIAN: ProductWalkthroughCopy = {
  experience: {
    title: 'Start Strong',
    subtitle: 'Authentic, relatable perspectives',
    descriptionDesktop:
      'Real voices and real stories from fellow believers remind us we’re not alone. Each audio and video experience offers understanding, encouragement, and validation to keep moving forward.',
    descriptionMobileBold: 'Real voices and real stories from fellow believers remind us we’re not alone ',
    descriptionMobileRest:
      'Each audio and video experience offers understanding, encouragement, and validation to keep moving forward.',
  },
  peersona: PRODUCT_WALKTHROUGH_KINDLY.peersona,
  calls: {
    title: 'Authentic Connections',
    subtitle: 'A safe space to talk it out',
    descriptionBold: 'Sometimes the best relief comes from saying things out loud.',
    descriptionBodyDesktop:
      ' When you connect with a Kindly Christian Peer you’ll speak with someone who listens with empathy, understands the role faith plays in life’s challenges, and offers perspective from a place of shared belief.',
    descriptionBodyMobile:
      ' When you connect with a Kindly Christian Peer you’ll speak with someone who listens with empathy, understands the role faith plays in life’s challenges, and offers perspective from a place of shared belief',
  },
  resources: {
    title: 'Resources Around You',
    subtitle: 'Unlocking the path forward',
    descriptionBold: "Life doesn't come with a roadmap.",
    descriptionBodyDesktop:
      ' The support you need is closer than you think. Kindly Christian helps connect you with resources available through your church or organization so you can find the right support and guidance, giving you clear next steps when you need them most. ',
    descriptionBodyMobile:
      ' The support you need is closer than you think. Kindly Christian helps connect you with resources available through your church or organization so you can find the right support and guidance, giving you clear next steps when you need them most.',
  },
};

const PRODUCT_WALKTHROUGH_BY_THEME: Record<ThemeId, ProductWalkthroughCopy> = {
  [THEME_ID.KINDLY]: PRODUCT_WALKTHROUGH_KINDLY,
  [THEME_ID.KINDLY_CHRISTIAN]: PRODUCT_WALKTHROUGH_KINDLY_CHRISTIAN,
};

export function getProductWalkthroughCopy(themeId: ThemeId): ProductWalkthroughCopy {
  return PRODUCT_WALKTHROUGH_BY_THEME[themeId];
}
